import React from 'react';

export const ModalContext = React.createContext(null);

const ModalProvider = ({ children, ...providerProps }) => {
  const [currentModal, setCurrentModal] = React.useState(null);
  const [queuedModals, setQueuedModals] = React.useState([]);

  const showModal = React.useCallback((modal, key) => {
    setCurrentModal(modal);

    if (key) {
      localStorage.setItem(key, JSON.stringify({ lastModalTime: Date.now() }));
    }
  }, []);

  const closeCurrentModal = React.useCallback(() => {
    setCurrentModal(null);
    setQueuedModals((prev) => {
      if (prev.length === 0) return prev;
      return prev.slice(1);
    });
  }, []);

  const addModalToQueue = React.useCallback((modal, key) => {
    setQueuedModals((prev) => [...prev, { modal, key }]);
  }, []);

  React.useEffect(() => {
    if (queuedModals.length === 0) return;
    if (currentModal) return;

    const [nextModal] = queuedModals;
    showModal(nextModal.modal, nextModal.key);
  }, [queuedModals, currentModal, showModal]);

  const getModalLastAlert = React.useCallback((key) => {
    if (!key) throw new Error('Key must be provided');

    const data = localStorage.getItem(key);

    if (!data) return null;

    const { lastModalTime } = JSON.parse(data);
    return lastModalTime;
  }, []);

  const value = React.useMemo(() => ({
    currentModal,
    setCurrentModal: (modal, key = '') => addModalToQueue(modal, key),
    closeCurrentModal,
    getModalLastAlert,
  }), [currentModal, addModalToQueue, closeCurrentModal, getModalLastAlert]);

  return (
    <ModalContext.Provider value={value} {...providerProps}>
      {children}
    </ModalContext.Provider>
  );
};

export const useModalContext = () => React.useContext(ModalContext);

export default ModalProvider;
